export function calculateDiscount(discountableSum, option) {
  if (!option) return 0

  const priceType = option.price_option?.priceType
  if (priceType !== 'With Discount') return 0

  const discounts = option.price_option?.discounts ?? []
  if (!discounts.length) return 0

  let result = discountableSum

  // Apply all dollar discounts first (accumulated), then all percent discounts (compounded)
  let totalDollar = 0
  let percentMultiplier = 1

  discounts.forEach(d => {
    if (d.priceType === 'Dollar discount') {
      totalDollar += Number(d.dollarDiscount) || 0
    } else if (d.priceType === 'Percent discount') {
      percentMultiplier *= (1 - (Number(d.percentDiscount) || 0) / 100)
    }
  })

  result = result - totalDollar
  result = result * percentMultiplier

  return Math.max(0, discountableSum - result)
}

export function calculatePaymentSums({ sessions,
                                       groups,
                                       products,
                                       paidAmount,
                                       additionalCharge,
}) {
  let discountableSum = 0
  let nonDiscountableSum = 0

  sessions.forEach(s => discountableSum += s.price)
  groups.forEach(g => discountableSum += g.price)

  products.forEach(p => {
    nonDiscountableSum += p.amount * (p.quantity || 1)
  })

  if (additionalCharge) {
    nonDiscountableSum += additionalCharge
  }

  let totalBeforeDiscount = discountableSum + nonDiscountableSum

  if (paidAmount) {
    nonDiscountableSum -= paidAmount
  }

  return {
    discountableSum,
    nonDiscountableSum,
    totalBeforeDiscount
  }
}

export function calculateTotalAmount({ discountableSum, nonDiscountableSum, discount })
{
  return discountableSum - discount + nonDiscountableSum
}

/**
 * Given the list of paymentFees from the API and the selected payment method,
 * return only the fees that apply to that payment method, each enriched with
 * a `calculatedAmount` field.
 *
 * @param {Array}         paymentFees          - full list of PaymentFee records for the program
 * @param {string}        paymentMethod        - the selected method e.g. 'Credit Card', 'ACH'
 * @param {number}        baseAmount           - the total before fees (for percentage calculation)
 * @param {string|Array|null} chargeTypeContext - when provided as a string or array of strings,
 *                                               only fees whose charge_types is empty/null OR
 *                                               has ANY overlap with the given context(s) are
 *                                               returned. When null/undefined every fee is included.
 * @returns {Array}
 */
export function calculateApplicableFees(paymentFees, paymentMethod, baseAmount, chargeTypeContext = null) {
  if (!paymentFees || !paymentFees.length || !paymentMethod) return []

  const activeContexts = chargeTypeContext === null
    ? null
    : (Array.isArray(chargeTypeContext) ? chargeTypeContext : [chargeTypeContext])

  return paymentFees
    .filter(fee => {
      // Must match the payment method
      if (!(fee.payment_types || []).includes(paymentMethod)) return false

      const types = fee.charge_types || []

      if (activeContexts === null) {
        // No context provided — only include fees that have no charge_types restriction (applies to all)
        return types.length === 0
      }

      // Context provided — include fees with no restriction OR with at least one matching context
      return types.length === 0 || types.some(t => activeContexts.includes(t))
    })
    .map(fee => {
      const calculatedAmount = fee.type === 'percentage'
        ? Math.round(((fee.value / 100) * baseAmount) * 100) / 100
        : Math.round(fee.value * 100) / 100
      return { ...fee, calculatedAmount }
    })
}
